
    import { computed, defineComponent, ref } from 'vue'
    import { useRouter } from 'vue-router'
    import { useSparePartFilterStore } from '@/store/sparePartFilter'
    import { useBrowserStateStore } from '@/store/browserState'

    import BitmapLink from '@/components/common/bitmap-link.vue'
    import MyIcon from '@/components/common/my-icon.vue'
    import { Button } from '@progress/kendo-vue-buttons'
    import { Push } from '@progress/kendo-vue-animation'

    import { buildClassificationLocalization, buildLocalization } from '@/util'
    import { PropertyValueDTO } from '@/store/classification'
    import { DbConstants } from '@/const'

    interface IProductModel {
        title: string,
        key: string
        bitmapPath: string,
        subItems: IProductModel[],
    }

    export default defineComponent({
        name: 'SparePartSearch',

        components: {
            BitmapLink,
            MyIcon,
            'k-button': Button,
            Push
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const sparePartFilterStore = useSparePartFilterStore()
            const browserState = useBrowserStateStore()
            const { localizePropValue } = buildClassificationLocalization()
            const router = useRouter()

            const sortOrder = [
                'GRYDE',
                'KIPSTEGER',
                'KOGEBORD',
                'INDUCS'
            ]

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const selectedKey = ref<string | null>(null)
            const showMainModelSeries = ref(true)
            const showSubModelSeries = ref(false)
            const mainDirection = ref('right')
            const subDirection = ref('right')

            const publicData = {
                selectedKey,
                showMainModelSeries,
                showSubModelSeries,
                mainDirection,
                subDirection,
            }

            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const isSmallScreen = computed(() => browserState.isSmallScreen)

            const modelPropertyValues = computed(() => sparePartFilterStore.allSparepartModelPropertyValues)

            /** Root model properties, excl. 'MANGLER' */
            const rootPropertyValues = computed(() => modelPropertyValues.value.filter(v => !v.parentPropertyValue && v.propertyValue != 'MANGLER'))

            /** Ordered root model properties, 'ANDET' always placed last */
            const orderedRootPropertyValues = computed(() => rootPropertyValues.value
                .sortBy(p => sortOrder.includes(p.propertyValue)
                    ? sortOrder.indexOf(p.propertyValue)
                    : sortOrder.length
                )
            )

            const productModels = computed(() => getProductModelInfo(orderedRootPropertyValues.value))

            const rootPropertyValuesDict = computed(() => modelPropertyValues.value
                .filter(v => v.parentPropertyValue != null)
                .toLookup(pv => pv.parentPropertyValue ?? '')
            )

            const selectedModelSeries = computed(() => productModels.value.find(m => m.key === selectedKey.value) ?? null)

            const computedData = {
                isSmallScreen,
                modelPropertyValues,
                rootPropertyValues,
                orderedRootPropertyValues,
                productModels,
                rootPropertyValuesDict,
                selectedModelSeries,
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function getProductModelInfo(rootProps: PropertyValueDTO[]): IProductModel[] {
                const result: IProductModel[] = []

                rootProps.forEach(rootProp => {
                    const children = childPropValues(rootProp)
                    const modelInfo = propToModelInfo(rootProp)

                    if (children.length) {
                        modelInfo.subItems = children
                            .map(child => propToModelInfo(child))
                            .sortBy(info => info.title)
                    }

                    result.push(modelInfo)
                })

                return result
            }

            function propToModelInfo(prop: PropertyValueDTO): IProductModel {
                return {
                    title: localizeValue(prop),
                    bitmapPath: bitmapPath(prop),
                    key: prop.propertyValue,
                    subItems: []
                }
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function localizeValue(propValueDTO: PropertyValueDTO): string {
                return localizePropValue(DbConstants.sparePartPropertyNames.sparePartModel, propValueDTO.propertyValue)
            }

            function childPropValues(propValueDTO: PropertyValueDTO): PropertyValueDTO[] {
                const result = rootPropertyValuesDict.value.get(propValueDTO.propertyValue) ?? []
                return result
            }

            function bitmapPath(propValueDTO: PropertyValueDTO): string {
                const normalizedPropertyValue = propValueDTO.propertyValue
                    .replaceAll(' ', '_')
                    .replaceAll('/', '-')
                    .replaceAll('\\', '-')
                    .replaceAll('+', 'plus')

                const path = require(`@/img/product-models/${normalizedPropertyValue}.jpg`)
                return path
            }

            function redirectToModel(model: IProductModel) {
                if (selectedKey.value === model.key) {
                    // same model re-selected
                    return
                }

                if (model.subItems.length) {
                    selectedKey.value = model.key
                    toggleViews()
                }
                else {
                    router.push({ name: 'sparepartOverview', params: { model: model.key } })
                }
            }

            function toggleViews() {
                if (showMainModelSeries.value) {
                    mainDirection.value = 'left'
                    subDirection.value = 'right'
                    showMainModelSeries.value = false

                    setTimeout(() => {
                        showSubModelSeries.value = true
                    }, 500)
                }
                else {
                    mainDirection.value = 'right'
                    subDirection.value = 'left'
                    showSubModelSeries.value = false

                    setTimeout(() => {
                        showMainModelSeries.value = true
                    }, 500)
                }
            }

            function closeSubItems() {
                selectedKey.value = null
                toggleViews()
            }

            const publicMethods = {
                localize,
                localizeValue,
                childPropValues,
                bitmapPath,
                redirectToModel,
                closeSubItems
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return {
                // DATA
                ...publicData,
                // COMPUTED
                ...computedData,
                // METHODS AND EVENT HANDLERS
                ...publicMethods
            }
        }
    })

